'use client';

/**
 * WebHeroCard — public landing-page hero, cards-everywhere port.
 *
 * Visual structure ported from
 * `klyde-website/src/components/landing/Hero.tsx`. Content moved to
 * YAML (`klyde_edge/content/data/web_landing.yaml`), validated by
 * Pydantic, served via the `web_landing_hero` BFF strategy resolved
 * through (category=content, action=web_landing_hero).
 *
 * No hardcoded strings — every label / headline / stat reads from
 * the spec. The framer-motion entry transitions, dark section
 * background, and column grid stay in this file as render concerns.
 *
 * Pilot scope: stub for KlydePlatformGraphic. The real graphic ports
 * over once the data loop is proven.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle, Sparkles } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebHeroCardContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';
import { MediaRenderer } from './media-renderer';


interface WebHeroCardProps {
  /** Capability action — landing defaults; for-suppliers passes
   *  'web_for_suppliers_hero'. Same renderer, different content. */
  strategyAction?: string;
}

export function WebHeroCard({
  strategyAction = 'web_landing_hero',
}: WebHeroCardProps = {}) {
  // Typography lives in the registry, not inline. WEB_HERO maps the
  // hero's roles into the seven-slot taxonomy:
  //   header   = eyebrow (uppercase mono, primary tint)
  //   name     = big display headline (size/weight only — colour per
  //              HeadlineLine.emphasis is content variation, applied here)
  //   body     = body copy
  //   metadata = fineprint
  //   amount   = stat value
  //   subtitle = stat label
  const T = useCardTypography();
  const heroStrategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebHeroCardContent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!heroStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    api
      .getStrategyData<WebHeroCardContent>(heroStrategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load hero content');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [heroStrategy, lang]);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Sparkles className="h-8 w-8 mb-2 opacity-40" />
        <p className={T.body}>Hero content not available.</p>
      </div>
    );
  }

  return (
    <section
      className={cn(
        'web-surface relative bg-background text-foreground overflow-hidden',
        // alignment='left' = Resources-style page intro; tighter vertical rhythm,
        // no viewport-filling. Default = full hero (landing / suppliers).
        data.alignment === 'left'
          ? 'pt-32 pb-12'
          : 'min-h-screen flex flex-col justify-center pt-20',
      )}
    >
      {/* Background atmosphere — radial glow + subtle grid pattern.
          Inline-styled rather than depending on the website's custom
          Tailwind classes (section-dark, hero-glow, line-pattern,
          grain-overlay) which don't exist in IPE's tailwind.config. */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            'radial-gradient(circle at 30% 20%, hsl(var(--primary) / 0.15) 0%, transparent 50%)',
        }}
      />

      {/* Layout branches first on graphic presence, then on alignment.
          Graphic-bearing heroes (landing) → 5-col split with text 2/5
          and graphic 3/5. No-graphic + alignment='center' (suppliers)
          → centered single column max-w-4xl. No-graphic + alignment='left'
          (Resources page-intro) → left-aligned max-w-2xl, no animation
          stats fanfare. Three modes, one component, branched on data. */}
      {data.graphic ? (
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">

            {/* Left column — text */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {data.eyebrow && (
                  <span className={cn(T.header, 'block mb-6')}>
                    {data.eyebrow}
                  </span>
                )}

                <h1 className={cn(T.name, 'mb-6')}>
                  {data.headline_lines.map((line, i) => (
                    <span
                      key={i}
                      className={cn(
                        'block',
                        line.emphasis ? 'text-primary' : 'text-foreground',
                      )}
                    >
                      {line.text}
                    </span>
                  ))}
                </h1>

                <p className={cn(T.body, 'max-w-xl mb-8 whitespace-pre-line')}>
                  {data.body}
                </p>

                {(data.cta || data.secondary_cta) && (
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    {data.cta && (
                      <a
                        href={data.cta.href}
                        className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 transition-all"
                      >
                        {data.cta.label}
                      </a>
                    )}
                    {data.secondary_cta && (
                      <a
                        href={data.secondary_cta.href}
                        className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3.5 text-base font-semibold text-foreground hover:bg-muted/30 transition-all"
                      >
                        {data.secondary_cta.label}
                      </a>
                    )}
                  </div>
                )}

                {data.fineprint && (
                  <p className={cn(T.metadata, 'mb-10')}>
                    {data.fineprint}
                  </p>
                )}
              </motion.div>
            </div>

            {/* Right column — media (resolved via media_ref slug → format-
                specific payload by MediaRenderer; renderer handles
                image/video/svg/lottie/react_component variants). */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <MediaRenderer
                mediaRef={data.graphic.media_ref}
                className="w-full"
              />
            </motion.div>

          </div>
        </div>
      ) : (
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className={cn(
              data.alignment === 'left'
                ? 'max-w-2xl text-left'
                : 'text-center max-w-4xl mx-auto',
            )}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {data.eyebrow && (
              <span className={cn(T.header, 'block mb-6')}>
                {data.eyebrow}
              </span>
            )}

            <h1 className={cn(T.name, 'mb-6')}>
              {data.headline_lines.map((line, i) => (
                <span
                  key={i}
                  className={cn(
                    'block',
                    line.emphasis ? 'text-primary' : 'text-foreground',
                  )}
                >
                  {line.text}
                </span>
              ))}
            </h1>

            <p
              className={cn(
                T.body,
                'mb-10 whitespace-pre-line',
                data.alignment === 'left' ? '' : 'max-w-2xl mx-auto',
              )}
            >
              {data.body}
            </p>

            {(data.cta || data.secondary_cta) && (
              <div
                className={cn(
                  'flex flex-col sm:flex-row gap-4 mb-4',
                  data.alignment === 'left' ? '' : 'justify-center',
                )}
              >
                {data.cta && (
                  <a
                    href={data.cta.href}
                    className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-base font-semibold text-primary-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {data.cta.label}
                  </a>
                )}
                {data.secondary_cta && (
                  <a
                    href={data.secondary_cta.href}
                    className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-base font-semibold text-foreground hover:bg-muted/30 transition-all"
                  >
                    {data.secondary_cta.label}
                  </a>
                )}
              </div>
            )}

            {data.fineprint && (
              <p className={cn(T.metadata, 'mb-10')}>
                {data.fineprint}
              </p>
            )}
          </motion.div>
        </div>
      )}

      {/* Stats bar */}
      {data.stats.length > 0 && (
        <motion.div
          className="w-full border-t border-b border-border/40 py-8 mt-12 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {data.stats.map((stat, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-between gap-1 h-[6rem]"
              >
                <span className={cn(T.amount, 'flex items-start justify-center')}>
                  {stat.value}
                </span>
                <span className={cn(T.subtitle, 'mt-auto')}>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </section>
  );
}


